import React from 'react';

import { createClassName } from 'utils/createClassName';

import './clientProductFieldsetStyles.scss';

type LayoutProps = {
  progress: React.ReactNode;
  agmRow: React.ReactNode;
  clientRow: React.ReactNode;
  partnerRow: React.ReactNode;
  summary: React.ReactNode;
};

export const ClientProductFieldsetLayoutComponent = (props: LayoutProps) => {
  const { progress, agmRow, clientRow, partnerRow, summary } = props;

  const createClientProductFieldsetLayoutClassName = createClassName(
    'clientProductFieldsetLayout'
  );

  const wrapperClassName = createClientProductFieldsetLayoutClassName(
    'wrapper'
  );

  const progressClassName = createClientProductFieldsetLayoutClassName(
    'progress'
  );

  const agmRowClassName = createClientProductFieldsetLayoutClassName('agmRow');

  const clientsRowClassName = createClientProductFieldsetLayoutClassName(
    'clientRow'
  );

  const partnerRowClassName = createClientProductFieldsetLayoutClassName(
    'partnerRow'
  );

  const summaryClassName = createClientProductFieldsetLayoutClassName(
    'summary'
  );

  return (
    <div className={wrapperClassName}>
      <div className={progressClassName}>{progress}</div>
      <div className={agmRowClassName}>{agmRow}</div>
      {partnerRow && <div className={partnerRowClassName}>{partnerRow}</div>}
      <div className={clientsRowClassName}>{clientRow}</div>
      <div className={summaryClassName}>{summary}</div>
    </div>
  );
};

type ProgressProps = {
  agmBarWidth: number;
  agmPercentText: React.ReactNode;
  partnerBarWidth: number;
  partnerPercentText: React.ReactNode;
  clientBarWidth: number;
  clientPercentText: React.ReactNode;
};

export const ClientProductFieldsetProgressComponent = (
  props: ProgressProps
) => {
  const {
    agmBarWidth,
    agmPercentText,
    partnerBarWidth,
    partnerPercentText,
    clientBarWidth,
    clientPercentText,
  } = props;

  const createClientProductFieldsetProgressClassName = createClassName(
    'clientProductFieldsetProgress'
  );

  const wrapperClassName = createClientProductFieldsetProgressClassName(
    'wrapper'
  );

  const legendClassName = createClientProductFieldsetProgressClassName(
    'legend'
  );

  const agmPercentClassName = createClientProductFieldsetProgressClassName(
    'agmPercent'
  );

  const partnerPercentClassName = createClientProductFieldsetProgressClassName(
    'partnerPercent'
  );

  const clientPercentClassName = createClientProductFieldsetProgressClassName(
    'clientPercent'
  );

  const barClassName = createClientProductFieldsetProgressClassName('bar');

  return (
    <div className={wrapperClassName}>
      <div className={legendClassName}>
        <div
          style={{ width: `${agmBarWidth}%` }}
          className={agmPercentClassName}
        >
          {agmPercentText}
        </div>
        <div
          style={{ width: `${partnerBarWidth}%` }}
          className={partnerPercentClassName}
        >
          {partnerPercentText}
        </div>
        <div
          style={{ width: `${clientBarWidth}%` }}
          className={clientPercentClassName}
        >
          {clientPercentText}
        </div>
      </div>
      <div className={barClassName}>
        <div style={{ width: `${agmBarWidth}%` }} />
        <div style={{ width: `${partnerBarWidth}%` }} />
        <div style={{ width: `${clientBarWidth}%` }} />
      </div>
    </div>
  );
};

type RowProps = {
  theme?: 'agm' | 'client' | 'partner';
  label: React.ReactNode;
  value: React.ReactNode;
  result: React.ReactNode;
};

export const ClientProductFieldsetRowComponent = (props: RowProps) => {
  const { theme, label, value, result } = props;

  const createClientProductFieldsetRowClassName = createClassName(
    'clientProductFieldsetRow'
  );

  const wrapperClassName = createClientProductFieldsetRowClassName('wrapper', {
    theme,
  });

  const labelClassName = createClientProductFieldsetRowClassName('label', {
    theme,
  });

  const valueClassName = createClientProductFieldsetRowClassName('value', {
    theme,
  });

  const resultClassName = createClientProductFieldsetRowClassName('result', {
    theme,
  });

  return (
    <div className={wrapperClassName}>
      <div className={labelClassName}>{label}</div>
      <div className={valueClassName}>{value}</div>
      <div className={resultClassName}>{result}</div>
    </div>
  );
};

type ValueProps = {
  percent: React.ReactNode;
};

export const ClientProductFieldsetValueComponent: React.FC<ValueProps> = (
  props
) => {
  const { children, percent } = props;

  const createClientProductFieldsetValueClassName = createClassName(
    'clientProductFieldsetValue'
  );

  const wrapperClassName = createClientProductFieldsetValueClassName('wrapper');

  const slotClassName = createClientProductFieldsetValueClassName('slot');

  const percentSymbolClassName = createClientProductFieldsetValueClassName(
    'percentSymbol'
  );

  return (
    <div className={wrapperClassName}>
      <div className={slotClassName}>{children}</div>
      <div className={percentSymbolClassName}>{percent}</div>
    </div>
  );
};
